import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import SupplierTabs from "../supplier/SupplierTabs"; // reuse your supplier tabs component

function SearchResults() {
  const [results, setResults] = useState([]);
  const [expandedId, setExpandedId] = useState(null);

  const [cogenDetails, setCogenDetails] = useState({});
  const [supplierDetails, setSupplierDetails] = useState({});
  const [classifiedDetails, setClassifiedDetails] = useState({});
  const [loading, setLoading] = useState(false);

  const [searchParams] = useSearchParams();
  const type = searchParams.get("type");
  const query = searchParams.get("q");

  useEffect(() => {
    const fetchResults = async () => {
      if (!type || !query) return;
      setLoading(true);
      try {
        const res = await axios.get("https://backend-zkgn.onrender.com/api/search", {
          params: { type, query },
        });
        setResults(res.data.results || []);
      } catch (err) {
        console.error(err);
        setResults([]);
      }
      setLoading(false);
    };
    fetchResults();
  }, [type, query]);

  const toggleExpand = async (item) => {
    const id = item.id;
    if (expandedId === id) {
      setExpandedId(null); // collapse
      return;
    }

    if (type === "Cogeneration Plant") {
      try {
        const [overviewRes, techRes, officialsRes, perfRes] = await Promise.all([
          axios.get(`https://backend-zkgn.onrender.com/api/cogen/${id}`),
          axios.get(`https://backend-zkgn.onrender.com/api/cogen_technical/${id}`),
          axios.get(`https://backend-zkgn.onrender.com/api/cogen_officials/${id}`),
          axios.get(`https://backend-zkgn.onrender.com/api/performance/cogen/${id}`),
        ]);

        setCogenDetails({
          overview: overviewRes.data,
          technical: techRes.data,
          officials: officialsRes.data,
          performance: perfRes.data,
        });
      } catch (err) {
        console.error("Failed to fetch cogeneration data:", err);
      }
    }

    if (type === "Supplier") {
      try {
        const res = await axios.get(`https://backend-zkgn.onrender.com/api/suppliers/${id}`);
        setSupplierDetails(res.data || {});
      } catch (err) {
        console.error("Failed to fetch supplier details:", err);
      }
    }

    if (type === "Classified") {
      try {
        // Fetch classified details
        const classifiedRes = await axios.get(`https://backend-zkgn.onrender.com/api/classified/${id}`);
        setClassifiedDetails(prev => ({ ...prev, [id]: classifiedRes.data }));

        // Fetch suppliers under this classified
        const suppliersRes = await axios.get(`https://backend-zkgn.onrender.com/api/suppliers/by-classified/${id}`);
        setSupplierDetails(prev => ({ ...prev, [id]: suppliersRes.data.suppliers }));
      } catch (err) {
        console.error("Failed to fetch classified details or suppliers:", err);
      }
    }

    setExpandedId(id);
  };

  return (
    <div className="container my-5">
      <h2 className="mb-4">Search Results for "{query}" in {type}</h2>

      {loading && <p>Loading...</p>}
      {!loading && results.length === 0 && <p>No results found.</p>}

      {!loading && results.length > 0 && (
        <ul className="list-group">
          {results.map((item) => (
            <li key={item.id} className="list-group-item">
              {/* Clickable Header */}
              <div
                style={{ cursor: type !== "Classified" ? "pointer" : "default" }}
                onClick={() => toggleExpand(item)}
              >
                {type === "Supplier" && <strong>{item.supplier_name} — {item.providers_of} ({item.city}, {item.state})</strong>}
                {type === "Classified" && <strong>{item.classified_name}</strong>}
                {type === "Cogeneration Plant" && <strong>{item.cogen_plant_name} — {item.city}, {item.state}</strong>}
              </div>

              {/* Cogeneration Tabs */}
              {expandedId === item.id && type === "Cogeneration Plant" && cogenDetails.overview && (
                <div className="mt-3 p-3 border rounded shadow-sm">
                  <ul className="nav nav-tabs mb-3" role="tablist">
                    <li className="nav-item">
                      <button className="nav-link active" data-bs-toggle="tab" data-bs-target={`#overview-${item.id}`}>Overview</button>
                    </li>
                    <li className="nav-item">
                      <button className="nav-link" data-bs-toggle="tab" data-bs-target={`#technical-${item.id}`}>Technical</button>
                    </li>
                    <li className="nav-item">
                      <button className="nav-link" data-bs-toggle="tab" data-bs-target={`#officials-${item.id}`}>Officials</button>
                    </li>
                    <li className="nav-item">
                      <button className="nav-link" data-bs-toggle="tab" data-bs-target={`#performance-${item.id}`}>Performance</button>
                    </li>
                  </ul>

                  <div className="tab-content">
                    <div className="tab-pane fade show active" id={`overview-${item.id}`}>
                      <table className="table table-bordered">
                        <tbody>
                          <tr><th>Plant Name</th><td>{cogenDetails.overview.cogen_plant_name}</td></tr>
                          <tr><th>Address</th><td>{cogenDetails.overview.plant_address}</td></tr>
                          <tr><th>Contact Number</th><td>{cogenDetails.overview.mobile_number}</td></tr>
                          <tr><th>Email</th><td>{cogenDetails.overview.email}</td></tr>
                        </tbody>
                      </table>
                    </div>

                    <div className="tab-pane fade" id={`technical-${item.id}`}>
                      <table className="table table-bordered">
                        <tbody>
                          <tr><th>Boiler Make</th><td>{cogenDetails.technical.boiler_make}</td></tr>
                          <tr><th>Boiler Capacity</th><td>{cogenDetails.technical.boiler_capacity}</td></tr>
                          <tr><th>Furnace Type</th><td>{cogenDetails.technical.furnes_type}</td></tr>
                          <tr><th>Fuel</th><td>{cogenDetails.technical.fuel}</td></tr>
                          <tr><th>Turbine Make</th><td>{cogenDetails.technical.turbine_make}</td></tr>
                          <tr><th>Turbine Capacity</th><td>{cogenDetails.technical.turbine_capacity}</td></tr>
                          <tr><th>Expansion</th><td>{cogenDetails.technical.cogen_expansion}</td></tr>
                          <tr><th>Capacity</th><td>{cogenDetails.technical.cogen_capacity}</td></tr>
                          <tr><th>Plant Make</th><td>{cogenDetails.technical.cogen_plant_make}</td></tr>
                        </tbody>
                      </table>
                    </div>

                    <div className="tab-pane fade" id={`officials-${item.id}`}>
                      <table className="table table-bordered">
                        <thead>
                          <tr><th>Designation</th><th>Name</th><th>Mobile</th><th>Phone</th><th>Email</th></tr>
                        </thead>
                        <tbody>
                          {cogenDetails.officials.map((o, idx) => (
                            <tr key={idx}>
                              <td>{o.designation}</td>
                              <td>{o.officials_name}</td>
                              <td>{o.mobile_no}</td>
                              <td>{o.phone_number}</td>
                              <td>{o.officials_email}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    <div className="tab-pane fade" id={`performance-${item.id}`}>
                      <table className="table table-bordered">
                        <thead>
                          <tr><th>Season</th><th>Bagasse Used</th><th>Power Generated</th><th>Power Exported</th><th>Operating Days</th></tr>
                        </thead>
                        <tbody>
                          {cogenDetails.performance.map((p, idx) => (
                            <tr key={idx}>
                              <td>{p.season}</td>
                              <td>{p.bgas_production}</td>
                              <td>{p.total_ecity_prod}</td>
                              <td>{p.total_ecity_export}</td>
                              <td>{p.working_days}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              )}

              {/* Supplier Tabs */}
              {expandedId === item.id && type === "Supplier" && supplierDetails && (
                <SupplierTabs supplier={supplierDetails} />
              )}

              {/* Classified Details + Suppliers */}
              {expandedId === item.id && type === "Classified" && classifiedDetails[item.id] && (
                <div className="mt-3 p-3 border rounded shadow-sm">
                  <h5>{classifiedDetails[item.id].classified_name}</h5>
                  <p><strong>Description:</strong> {classifiedDetails[item.id].description}</p>

                  {supplierDetails[item.id]?.length > 0 ? (
                    supplierDetails[item.id].map(s => (
                      <SupplierTabs key={s.id} supplier={s} />
                    ))
                  ) : (
                    <p>No suppliers found for this classified.</p>
                  )}
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default SearchResults;
